import {
    useState,
    useRef,
    useCallback,
    useEffect,
    ChangeEvent,
    DragEvent,
} from 'react'

interface UseFileDropOptions {
    onFiles: (files: File[]) => void
    multiple?: boolean
    preventWindowDrop?: boolean
}

interface UseFileDropResult {
    isDragging: boolean
    inputRef: React.RefObject<HTMLInputElement>
    openPicker: () => void
    dropHandlers: {
        onDragEnter: (e: DragEvent<HTMLElement>) => void
        onDragOver: (e: DragEvent<HTMLElement>) => void
        onDragLeave: (e: DragEvent<HTMLElement>) => void
        onDrop: (e: DragEvent<HTMLElement>) => void
    }
    handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void
}

export function useFileDrop({
    onFiles,
    multiple = false,
    preventWindowDrop = false,
}: UseFileDropOptions): UseFileDropResult {
    const [isDragging, setIsDragging] = useState(false)
    const dragDepthRef = useRef(0)
    const inputRef = useRef<HTMLInputElement>(null)

    const handleDragEnter = useCallback((e: DragEvent<HTMLElement>) => {
        e.preventDefault()
        dragDepthRef.current += 1
        if (e.dataTransfer.types.includes('Files')) {
            setIsDragging(true)
        }
    }, [])

    const handleDragOver = useCallback((e: DragEvent<HTMLElement>) => {
        e.preventDefault()
    }, [])

    const handleDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
        e.preventDefault()
        dragDepthRef.current -= 1
        if (dragDepthRef.current === 0) {
            setIsDragging(false)
        }
    }, [])

    const handleDrop = useCallback(
        (e: DragEvent<HTMLElement>) => {
            e.preventDefault()
            setIsDragging(false)
            dragDepthRef.current = 0

            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                const dropped = Array.from(e.dataTransfer.files)
                const filesToApply = multiple ? dropped : dropped.slice(0, 1)
                onFiles(filesToApply)
            }
        },
        [multiple, onFiles]
    )

    const handleInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const selected = Array.from(e.target.files ?? [])
            if (selected.length > 0) {
                onFiles(selected)
            }
        },
        [onFiles]
    )

    const openPicker = useCallback(() => {
        inputRef.current?.click()
    }, [])

    useEffect(() => {
        if (!preventWindowDrop) return undefined

        const handleWindowDragOver = (e: globalThis.DragEvent) => {
            e.preventDefault()
        }
        const handleWindowDrop = (e: globalThis.DragEvent) => {
            e.preventDefault()
        }
        window.addEventListener('dragover', handleWindowDragOver)
        window.addEventListener('drop', handleWindowDrop)
        return () => {
            window.removeEventListener('dragover', handleWindowDragOver)
            window.removeEventListener('drop', handleWindowDrop)
        }
    }, [preventWindowDrop])

    return {
        isDragging,
        inputRef,
        openPicker,
        dropHandlers: {
            onDragEnter: handleDragEnter,
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
        },
        handleInputChange,
    }
}
